import * as Sentry from "@sentry/react";

/**
 * Coerce an unknown thrown value into a real `Error` with a meaningful message.
 *
 * Plain objects (notably Supabase/PostgREST `PostgrestError`, which is NOT an
 * `Error` instance) would otherwise hit `String(error)` and serialize to the
 * literal "[object Object]". That hid the real cause AND collapsed every
 * unrelated failure into a single Sentry issue. Here we pull out a useful
 * message and surface the structured fields (code/details/hint/status) so they
 * survive into Sentry.
 */
function normalizeError(error: unknown): {
  error: Error;
  extra?: Record<string, unknown>;
} {
  if (error instanceof Error) return { error };

  if (typeof error === "string") return { error: new Error(error) };

  if (error && typeof error === "object") {
    const obj = error as Record<string, unknown>;
    const message =
      typeof obj.message === "string" && obj.message.length > 0
        ? obj.message
        : safeStringify(obj);

    const err = new Error(message);

    // Distinguish by error code (e.g. "PGRST202", "23505") so distinct
    // failures group separately in Sentry instead of all under "Error".
    if (typeof obj.code === "string" && obj.code.length > 0) {
      err.name = `PostgrestError[${obj.code}]`;
    }

    const extra: Record<string, unknown> = {};
    for (const key of ["code", "details", "hint", "status", "statusText"]) {
      if (obj[key] !== undefined) extra[key] = obj[key];
    }

    return {
      error: err,
      extra: Object.keys(extra).length > 0 ? extra : undefined,
    };
  }

  return { error: new Error(String(error)) };
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * Capture an error and send it to Sentry (staging/production).
 * In local development, logs to console.error for visibility.
 */
export function captureError(
  error: unknown,
  context?: {
    tags?: Record<string, string>;
    extra?: Record<string, unknown>;
  }
) {
  const { error: err, extra: derivedExtra } = normalizeError(error);
  const extra = { ...derivedExtra, ...context?.extra };

  // Always log to console in development.
  // Optional chaining because `import.meta.env` is a bundler-provided object, not a
  // language guarantee: a bare `.DEV` throws wherever this module is loaded outside a
  // Vite build and masks the real diagnostic with a TypeError. The concrete case was a
  // Node script importing the overdue calculator through here; the calculator moved into
  // the database (fleet-backend D-T25) and that script is gone, but the guard is kept —
  // it costs one `?` and the failure it prevents is a swallowed error report.
  if (import.meta.env?.DEV) {
    console.error(err, extra);
  }

  Sentry.withScope((scope) => {
    if (context?.tags) {
      Object.entries(context.tags).forEach(([k, v]) => scope.setTag(k, v));
    }
    Object.entries(extra).forEach(([k, v]) => scope.setExtra(k, v));
    Sentry.captureException(err);
  });
}
